import { createStoreHooks, type FeatureDeps } from '../../../../../canvas/core';
import type { PageLayoutData } from '../../../../core';
import { createTranslationLayerFeature } from '../../feature';
import { usePageTranslations } from '../../hooks/usePageTranslations';
import { ActiveTranslationOverlay } from './ActiveTranslationOverlay';
import { useActiveTranslation } from './useActiveTranslation';
import { useTranslationsCanvasRenderer } from './useTranslationsCanvasRenderer';
import { useTranslationsViewModels } from './useTranslationsViewModels';

type TranslationsOverlayDeps = FeatureDeps<
  [typeof createTranslationLayerFeature]
>;

const { useStore } = createStoreHooks<TranslationsOverlayDeps>();

type TranslationsOverlayProps = {
  pageNumber: number;
  pageLayout: PageLayoutData;
  locale?: string;
};

export const TranslationsOverlay = ({
  pageNumber,
  pageLayout,
  locale = 'cs',
}: TranslationsOverlayProps) => {
  const activeRectId = useStore(state =>
    state.translationLayer.state.activePageId === pageLayout.pageId
      ? state.translationLayer.state.activeRectId
      : null
  );

  const { data: rects = [] } = usePageTranslations(pageNumber, locale);
  const viewModels = useTranslationsViewModels(rects, activeRectId);
  const activeViewModel = viewModels.find(vm => vm.isActive) ?? null;

  useActiveTranslation(pageLayout, viewModels, activeRectId);
  useTranslationsCanvasRenderer(
    pageNumber,
    pageLayout,
    viewModels,
    activeRectId
  );

  return (
    <foreignObject
      x={0}
      y={0}
      width={pageLayout.originalWidth}
      height={pageLayout.originalHeight}
      style={{ overflow: 'visible', pointerEvents: 'none' }}
    >
      <ActiveTranslationOverlay activeViewModel={activeViewModel} />
    </foreignObject>
  );
};
